/**
 * Purchase & inventory (Phase 3): requisitions, purchase orders, goods receipts (with optional quality
 * check), supplier bills, purchase returns, stock issues / returns / transfers / adjustments, the stock
 * movement ledger (moving weighted average) and month-end closing stock.
 */
import { z } from 'zod';
import { round2 } from './accounting';
import { always } from './schemas';

const opt = (max: number) => z.string().trim().max(max, `At most ${max} characters`).optional().default('');
const req = (label: string, max: number) => z.string().trim().min(1, `${label} is required`).max(max, `${label} must be at most ${max} characters`);
const isoDate = z.string({ error: 'Enter a date' }).regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date');
const optDate = z.union([z.literal(''), isoDate]).nullable().optional().transform((v) => v || null);
const uuid = (msg: string) => z.string({ error: msg }).uuid(msg);
const uuidOpt = z.string().uuid().nullable().optional().transform((v) => v ?? null);
/** Quantities: up to 4 decimals (the unit's own decimals are checked on the server). */
const qty = (label = 'Quantity') =>
  z.preprocess((v) => (v === '' || v == null ? undefined : v), z.coerce.number({ error: `Enter the ${label.toLowerCase()}` }).max(1e9, `${label} is too large`).refine((n) => Math.round(n * 10000) === n * 10000 || Math.abs(Math.round(n * 10000) - n * 10000) < 1e-6, `${label} has at most 4 decimals`));
const positiveQty = (label = 'Quantity') => qty(label).refine((n) => n > 0, `${label} must be above 0`);
const rate = (label = 'Rate') => z.preprocess((v) => (v === '' || v == null ? 0 : v), z.coerce.number({ error: `Enter the ${label.toLowerCase()}` }).min(0, `${label} cannot be negative`).max(1e9));
export const round4 = (n: number) => Math.round((n + Number.EPSILON) * 10000) / 10000;

/** Net, VAT and total of a line (VAT on the net after discount, rounded to the fil per line). */
export function lineAmounts(q: number, r: number, discountPct: number, taxRate: number) {
  const gross = round2(q * r);
  const net = round2(gross - round2((gross * (discountPct || 0)) / 100));
  const tax = round2((net * (taxRate || 0)) / 100);
  return { net, tax, total: round2(net + tax) };
}

// ---------- Purchase requisition ----------

/**
 * What a new-system requisition is for; old-system documents have none ("Old system"). Against an order: the order's
 * jobs (furniture) and mattress lines (from their BOM), all or some of them, one department or some items at a time.
 * Special request: the store's special request notes for an order.
 */
export const REQUISITION_KINDS = ['ORDER', 'GENERAL', 'STOCK', 'SPECIAL'] as const;
export type RequisitionKind = (typeof REQUISITION_KINDS)[number];
export const PURCHASE_KIND_LABELS: Record<string, string> = { ORDER: 'Against order', GENERAL: 'General', STOCK: 'Stock purpose', SPECIAL: 'Special request for order', URGENT: 'Urgent (old)', SERVICE: 'Service (old)' };

export const prLineSchema = z.object({
  id: z.string().uuid().optional(),
  itemId: uuid('Select an item'),
  /** To buy. Against an order it starts at the system's demand and may be 0 when stock covers it. */
  qty: qty().refine((n) => n >= 0, 'Cannot be negative'),
  /** Why the demand the system worked out was changed (required when it was). */
  overrideReason: opt(300),
  /** The store's special request note line it takes up. */
  srnLineId: uuidOpt,
  description: opt(300),
  requiredDate: optDate,
  notes: opt(300),
});

/** Against an order: which part of the order this requisition plans (empty = all of it). */
export const requisitionScopeSchema = z.object({
  jobIds: z.array(z.string().uuid()).max(200).default([]),
  soLineIds: z.array(z.string().uuid()).max(500).default([]),
  departmentId: uuidOpt,
  itemIds: z.array(z.string().uuid()).max(500).default([]),
});
export type RequisitionScope = z.infer<typeof requisitionScopeSchema>;

export const purchaseRequisitionSchema = z.object({
  kind: z.enum(REQUISITION_KINDS).default('STOCK'),
  orderId: uuidOpt,
  scope: requisitionScopeSchema.default({ jobIds: [], soLineIds: [], departmentId: null, itemIds: [] }),
  /** The department asking (for general and stock requisitions, and to filter an order's material). */
  departmentId: uuidOpt,
  docDate: isoDate,
  requiredDate: isoDate,
  warehouseId: uuid('Select the warehouse'),
  reference: opt(100),
  notes: opt(500),
  lines: z.array(prLineSchema).min(1, 'Add at least one item').max(500),
}).refine((v) => v.requiredDate >= v.docDate, { path: ['requiredDate'], message: 'Required date cannot be before the requisition date', ...always })
  .refine((v) => !['ORDER', 'SPECIAL'].includes(v.kind) || !!v.orderId, { path: ['orderId'], message: 'Choose the order', ...always })
  .refine((v) => v.kind === 'ORDER' || v.lines.every((l) => l.qty > 0), { path: ['lines'], message: 'Every quantity must be above 0', ...always });
export type PurchaseRequisitionInput = z.infer<typeof purchaseRequisitionSchema>;

// ---------- Purchase order ----------

export const poLineSchema = z.object({
  id: z.string().uuid().optional(),
  itemId: uuid('Select an item'),
  prLineId: uuid('Each line comes from the requisition'),
  qty: positiveQty(),
  rate: rate(),
  discountPct: z.preprocess((v) => (v === '' || v == null ? 0 : v), z.coerce.number().min(0, 'Cannot be negative').max(100, 'At most 100%')),
  /** Set by the system from the order's VAT mode (the VAT rate of Accounting's standard code); kept for old documents. */
  taxCodeId: uuidOpt,
  /** A rate of 0 needs the reason (free of charge, sample…). */
  notes: opt(300),
}).refine((l) => l.rate > 0 || l.notes.trim().length >= 3, { path: ['notes'], message: 'Rate is 0: enter the agreed price, or say why it is free of charge' });

/**
 * VAT on a purchase order, for the whole order: without VAT (default; vendors without a TRN can only be this), VAT
 * added on top of the rates, or VAT already inside the rates (the system takes it out: stock is valued without VAT).
 */
export const PO_VAT_MODES = ['NONE', 'EXCLUSIVE', 'INCLUSIVE'] as const;
export type PoVatMode = (typeof PO_VAT_MODES)[number];
export const PO_VAT_MODE_LABELS: Record<PoVatMode, string> = { NONE: 'Without VAT', EXCLUSIVE: 'VAT added (exclusive)', INCLUSIVE: 'VAT included in rate' };
/** A bill from VAT-included purchase orders may differ from the supplier's total by this much (rounding of the rates). */
export const BILL_INCLUSIVE_ROUNDING = 0.05;
export const PO_VAT_MODE_HINTS: Record<PoVatMode, string> = { NONE: 'The rates are the full cost; no VAT.', EXCLUSIVE: 'VAT is added on top of the rates.', INCLUSIVE: 'The rates already include VAT; it is taken out to value the stock.' };

/** A purchase order is made from one approved requisition: its lines for this vendor, never more than is open. */
export const purchaseOrderSchema = z.object({
  requisitionId: uuid('Choose the requisition'),
  docDate: isoDate,
  supplierId: uuid('Select the vendor'),
  vatMode: z.enum(PO_VAT_MODES).default('NONE'),
  paymentTermId: uuid('Select payment terms'),
  warehouseId: uuid('Select the delivery warehouse'),
  deliveryDate: optDate,
  supplierRef: opt(60),
  notes: opt(1000),
  lines: z.array(poLineSchema).min(1, 'Add at least one item').max(300),
}).refine((v) => !v.deliveryDate || v.deliveryDate >= v.docDate, { path: ['deliveryDate'], message: 'Delivery date cannot be before the order date', ...always });
export type PurchaseOrderInput = z.infer<typeof purchaseOrderSchema>;

// ---------- Goods receipt ----------

export const grnLineSchema = z
  .object({
    id: z.string().uuid().optional(),
    itemId: uuid('Select an item'),
    poLineId: uuidOpt,
    qtyReceived: positiveQty('Quantity received'),
    /** Only when the item's category requires a quality check; otherwise everything received is accepted. */
    qtyRejected: z.preprocess((v) => (v === '' || v == null ? 0 : v), z.coerce.number().min(0, 'Cannot be negative')),
    unitCost: rate('Unit cost'),
    taxCodeId: uuid('Select a VAT code'),
    rejectReason: opt(200),
    /** Rejected quantity: a replacement is expected (back on the PO) or it will not be replaced (closed short). */
    rejectAction: z.enum(['REPLACE', 'CLOSE']).default('REPLACE'),
    /** Receiving more than ordered (within the category's extra-receipt %): why. */
    overReason: opt(200),
    /** Less received than arrived at the gate: why (the difference stays open on the PO). */
    shortReason: opt(200),
    /** Bill rate different from the PO rate: why (only users allowed to override the rate). */
    rateReason: opt(200),
    notes: opt(300),
  })
  .refine((l) => l.qtyRejected <= l.qtyReceived, { path: ['qtyRejected'], message: 'Rejected cannot exceed received', ...always })
  .refine((l) => !l.qtyRejected || !!l.rejectReason, { path: ['rejectReason'], message: 'Give the reason for rejecting', ...always });

export const goodsReceiptSchema = z.object({
  docDate: isoDate,
  supplierId: uuid('Select the supplier'),
  poId: uuidOpt,
  /** The inward gate pass it is made from (every goods receipt comes from one; a service PO is confirmed without). */
  gatePassId: uuidOpt,
  /** The supplier's bill: number and date, both mandatory (owner, 2026-11-02). */
  billNo: z.string({ error: "Enter the supplier's bill no." }).trim().min(1, "Enter the supplier's bill no.").max(40),
  billDate: isoDate,
  warehouseId: uuid('Select the receiving warehouse'),
  supplierDnNo: opt(60),
  vehicleNo: opt(30),
  notes: opt(1000),
  lines: z.array(grnLineSchema).min(1, 'Add at least one item').max(300),
});
export type GoodsReceiptInput = z.infer<typeof goodsReceiptSchema>;

// ---------- Supplier bill ----------

export const billLineSchema = z
  .object({
    id: z.string().uuid().optional(),
    /** Goods: the receipt line being billed. Services/expenses: an account instead. */
    grnLineId: uuidOpt,
    itemId: uuidOpt,
    accountId: uuidOpt,
    costCentreId: uuidOpt,
    description: opt(300),
    qty: positiveQty(),
    rate: rate(),
    discountPct: z.preprocess((v) => (v === '' || v == null ? 0 : v), z.coerce.number().min(0).max(100)),
    taxCodeId: uuid('Select a VAT code'),
  })
  .refine((l) => !!l.grnLineId || !!l.accountId || !!l.itemId, { path: ['accountId'], message: 'Choose a received item or an expense account', ...always });

export const purchaseBillSchema = z.object({
  docDate: isoDate,
  supplierId: uuid('Select the supplier'),
  billNo: req("Supplier's bill number", 40),
  dueDate: optDate,
  /** The total on the supplier's bill (required when the bill has goods from a VAT-included purchase order). */
  supplierTotal: z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number({ error: "Enter the total on the supplier's bill" }).min(0, 'The total cannot be negative').max(1e12).nullable()).optional().default(null),
  /** Required when a line's VAT differs from its purchase order's (checked by the server). */
  vatOverrideReason: opt(300),
  notes: opt(1000),
  lines: z.array(billLineSchema).min(1, 'Add at least one line').max(300),
});
export type PurchaseBillInput = z.infer<typeof purchaseBillSchema>;

// ---------- Purchase return ----------

export const returnLineSchema = z.object({
  id: z.string().uuid().optional(),
  /** Goods returned (against a goods receipt)… */
  itemId: uuidOpt,
  grnLineId: uuidOpt,
  /** …or part of an expense / non-stock line of a supplier bill (LPV) credited back. */
  billLineId: uuidOpt,
  accountId: uuidOpt,
  costCentreId: uuidOpt,
  description: opt(300),
  qty: positiveQty(),
  rate: rate(),
  taxCodeId: uuid('Select a VAT code'),
  notes: opt(300),
});

/**
 * Purchase return (LPR): goods back to the supplier against a goods receipt (needs the warehouse), or — with
 * `billId` — a credit on the expense / non-stock lines of a supplier bill (no stock moves).
 */
export const purchaseReturnSchema = z
  .object({
    docDate: isoDate,
    supplierId: uuid('Select the supplier'),
    warehouseId: uuidOpt,
    grnId: uuidOpt,
    billId: uuidOpt,
    reason: req('Reason', 300),
    lines: z.array(returnLineSchema).min(1, 'Add at least one line').max(300),
  })
  .refine((v) => !!v.billId || !!v.warehouseId, { path: ['warehouseId'], message: 'Select the warehouse', ...always })
  .refine((v) => (v.billId ? v.lines.every((l) => !!l.billLineId && !l.itemId) : v.lines.every((l) => !!l.itemId)), { path: ['lines'], message: 'Return goods against a goods receipt, or expense lines against a supplier bill — not both', ...always });
export type PurchaseReturnInput = z.infer<typeof purchaseReturnSchema>;

// ---------- Stock documents ----------

export const STOCK_DOC_KINDS = ['issue', 'return', 'transfer', 'adjustment', 'opening', 'conversion'] as const;
export type StockDocKind = (typeof STOCK_DOC_KINDS)[number];
export const STOCK_DOC_TYPE: Record<StockDocKind, string> = {
  issue: 'inv.stock_issue',
  return: 'inv.stock_return',
  transfer: 'inv.stock_transfer',
  adjustment: 'inv.stock_adjustment',
  opening: 'inv.opening_stock',
  conversion: 'inv.item_conversion',
};
export const ADJUSTMENT_REASONS = ['COUNT', 'DAMAGE', 'EXPIRY', 'RECODE', 'CORRECTION', 'OTHER'] as const;
export const ADJUSTMENT_REASON_LABELS: Record<(typeof ADJUSTMENT_REASONS)[number], string> = {
  COUNT: 'Physical count',
  DAMAGE: 'Damaged / scrapped',
  EXPIRY: 'Expired',
  RECODE: 'Wrong item code (move to the right item)',
  CORRECTION: 'Correction of an earlier error',
  OTHER: 'Other',
};

export const stockLineSchema = z.object({
  id: z.string().uuid().optional(),
  itemId: uuid('Select an item'),
  /** Adjustments: positive adds stock, negative removes it. Other kinds: always positive. */
  qty: qty().refine((n) => n !== 0, 'Quantity cannot be 0'),
  /** Adjustments that add stock / opening stock: value per unit. Empty = current average cost. */
  unitCost: z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number().min(0, 'Cannot be negative').max(1e9).nullable()),
  notes: opt(300),
  /** Issue / return against an order: how the quantity splits over its jobs (empty = the system splits it). */
  split: z.array(z.object({ jobId: uuidOpt, soLineId: uuidOpt, qty: qty().refine((n) => n >= 0, 'Cannot be negative') })).max(200).default([]),
  /** Above what the plan leaves: why (an extra issue, approved). */
  extraReason: opt(200),
});

/** What an issue (or return from production) is for: general items to a section, or an order's material. */
export const ISSUE_PURPOSES = ['GENERAL', 'ORDER'] as const;

export const stockDocSchema = z
  .object({
    kind: z.enum(STOCK_DOC_KINDS),
    purpose: z.enum(ISSUE_PURPOSES).nullable().optional().transform((v) => v ?? null),
    /** Against an order: the order and the jobs (furniture) / mattress lines the material goes to. */
    orderId: uuidOpt,
    jobIds: z.array(z.string().uuid()).max(200).default([]),
    docDate: isoDate,
    warehouseId: uuid('Select the warehouse'),
    toWarehouseId: uuidOpt,
    departmentId: uuidOpt,
    costCentreId: uuidOpt,
    reference: opt(100),
    reason: z.enum(ADJUSTMENT_REASONS).nullable().optional().transform((v) => v ?? null),
    notes: opt(1000),
    lines: z.array(stockLineSchema).min(1, 'Add at least one item').max(1000),
  })
  .refine((v) => v.kind !== 'transfer' || !!v.toWarehouseId, { path: ['toWarehouseId'], message: 'Select the warehouse to transfer to', ...always })
  .refine((v) => v.kind !== 'transfer' || v.toWarehouseId !== v.warehouseId, { path: ['toWarehouseId'], message: 'Choose a different warehouse', ...always })
  .refine((v) => !['issue', 'return'].includes(v.kind) || !!v.departmentId, { path: ['departmentId'], message: 'Select the section / department', ...always })
  .refine((v) => v.kind !== 'adjustment' || !!v.reason, { path: ['reason'], message: 'Select the reason', ...always })
  .refine((v) => v.purpose !== 'ORDER' || !!v.orderId, { path: ['orderId'], message: 'Choose the order', ...always })
  .refine((v) => v.purpose !== 'GENERAL' || v.kind !== 'issue' || !!v.costCentreId, { path: ['costCentreId'], message: 'Select the cost centre the general items are charged to', ...always })
  .refine((v) => v.kind === 'adjustment' || v.kind === 'conversion' || v.lines.every((l) => l.qty > 0), { path: ['lines'], message: 'Quantities must be above 0', ...always })
  .refine((v) => v.kind !== 'conversion' || (v.lines.some((l) => l.qty < 0) && v.lines.some((l) => l.qty > 0)), { path: ['lines'], message: 'A conversion takes at least one item out (−) and puts at least one item in (+)', ...always });
export type StockDocInput = z.infer<typeof stockDocSchema>;

// ---------- Responses ----------

export interface InvItemRef {
  /** Purchase unit and how many stock units it holds (e.g. ROLL = 50 MTR). */
  purchase?: { uom: string; factor: number } | null;
  id: string;
  code: string;
  name: string;
  uom: string;
  decimals: number;
  isStockTracked: boolean;
}

export interface InvDocLine {
  id: string;
  lineNo: number;
  item: InvItemRef | null;
  account: { id: string; code: string; name: string } | null;
  description: string;
  qty: number;
  rate: number;
  discountPct: number;
  taxCodeId: string | null;
  taxRate: number;
  /** Purchase bill line from a goods receipt: its purchase order's VAT code (a different taxCodeId is an override). */
  originalTaxCodeId?: string | null;
  net: number;
  tax: number;
  total: number;
  /** Goods receipt: received / rejected / accepted. */
  qtyReceived?: number;
  qtyRejected?: number;
  rejectReason?: string;
  inspect?: boolean;
  unitCost?: number | null;
  /** Links and progress. */
  sourceLineId: string | null;
  sourceNumber: string | null;
  orderId?: string | null;
  job?: { id: string; number: string | null } | null;
  department?: string | null;
  planQty?: number | null;
  systemQty?: number | null;
  /** The demand was changed by a person (shown in red on every later document). */
  overrideReason?: string;
  fromStock?: number;
  /** From the store's special request note (highlighted on every later document). */
  special?: { srnLineId: string; number: string | null } | null;
  /** Supplier bills: the goods receipt's date and its purchase order. */
  sourceDate?: string | null;
  sourcePoNumber?: string | null;
  done?: number;
  open?: number;
  rejectAction?: string;
  overReason?: string;
  shortReason?: string;
  /** Goods receipt: the PO's rate (without VAT, after discount); the line's rate is the bill rate, and why it differs. */
  poRate?: number | null;
  rateReason?: string;
  /** Goods receipt: what arrived at the gate on the gate pass for this line. */
  gateQty?: number | null;
  /** More than ordered (owner, 2026-11-02: shown in red everywhere): on the PO line within the allowed %, or on a Sub PO. */
  extra?: { qty: number; subPo: string | null } | null;
  /** Sub PO line: the parent PO's line. PO line: extra ordered on its Sub POs. */
  parentLineId?: string | null;
  subPoQty?: number;
  /** Purchase order line: the last price paid for the item (without VAT) on another approved PO, any vendor. */
  lastRate?: { rate: number; number: string | null; date: string; vendor: string | null } | null;
  extraReason?: string;
  heldQty?: number;
  /** Quantity closed short (not to be ordered / received). */
  closedQty?: number;
  notes: string;
  costCentreId?: string | null;
}

export interface InvDocDetail {
  /** Requisition / PO / issue purpose (null = old system), and the order it is for. */
  kind?: string | null;
  order?: { id: string; number: string | null; customer: string | null } | null;
  /** Requisition: the part of the order it plans. PO: its requisition. */
  scope?: { jobs: { id: string; number: string | null }[]; departmentId: string | null } | null;
  requisition?: { id: string; number: string | null; date?: string } | null;
  /** Sent back for correction: the reason and who sent it back (latest). */
  sentBack?: { reason: string; by: string | null; at: string } | null;
  /** Purchase order / its receipts: VAT mode (null = old document, VAT per line) and the VAT rate it used. */
  vatMode?: PoVatMode | null;
  vatRate?: number | null;
  /** Purchase order: the vendor has a TRN (needed to charge VAT). */
  supplierTrn?: string | null;
  /** Purchase order: its inward gate passes — once there is one, the PO is fixed (no change, send back or cancel). */
  gatePasses?: string[];
  /** Goods receipt: the inward gate pass it came through. */
  gatePass?: { id: string; number: string | null } | null;
  igpRequired?: boolean | null;
  /** Old-system history (view only): everything the old ERP recorded on it. */
  history?: boolean;
  legacy?: InvLegacyInfo | null;
  /** Sub PO: the PO it adds extra to and why. PO: its Sub POs. */
  parentPo?: { id: string; number: string | null } | null;
  subPos?: { id: string; number: string | null; status: import('./documents').DocStatus; total: number; reason: string }[];
  /** Goods receipt / PO: total quantity above the order (Sub PO lines included), for the red flag. */
  extraQty?: number;
  /** Purchase orders: change requests (old → new), newest first. */
  poChanges?: PoChangeRow[];
  /** Supplier bills: what the approver must check (VAT vs order, above the order, dearer than last time). */
  checks?: string[];
  id: string;
  docType: string;
  number: string | null;
  docDate: string;
  status: import('./documents').DocStatus;
  supplier: { id: string; code: string; name: string } | null;
  warehouseId: string | null;
  toWarehouseId: string | null;
  departmentId: string | null;
  costCentreId: string | null;
  paymentTermId: string | null;
  sourceId: string | null;
  sourceNumber: string | null;
  fields: Record<string, string | null>;
  lines: InvDocLine[];
  net: number;
  tax: number;
  total: number;
  /** Ordered/received/billed progress for PR/PO/GRN. */
  progress: { label: string; percent: number } | null;
  /** Requisition / PO closed with quantity still open. */
  closed: { at: string; reason: string; by: string } | null;
  journalId: string | null;
  migrated: boolean;
  createdBy: string | null;
  createdAt: string;
  version: number;
  actions: import('./documents').WorkflowAction[];
}

export interface InvDocListItem {
  /** Sub PO: the number of the PO it adds extra to (shown in red). */
  subPoOf?: string | null;
  /** Old-system history (view only) and what the old ERP recorded. */
  history?: boolean;
  legacy?: { poNo: string | null; igpNo: string | null; mrnNo: string | null; issuedTo: string | null; section: string | null; oldStatus: string | null } | null;
  /** PO: its requisition. PO / requisition: the job numbers it serves. */
  requisition?: { number: string | null; date: string } | null;
  jobs?: string[];
  id: string;
  docType: string;
  number: string | null;
  docDate: string;
  status: import('./documents').DocStatus;
  party: string | null;
  warehouse: string | null;
  reference: string;
  total: number;
  progress: string | null;
  migrated: boolean;
  /** Purpose (null = old system). */
  kind?: string | null;
  /** Goods receipts: made through a gate pass. */
  withGatePass?: boolean;
  version?: number;
  /** Item lines. */
  lines?: number;
  order?: { id: string; number: string | null; customer: string | null } | null;
  department?: string | null;
  /** Requisition: needed by; PO: delivery date. */
  dueDate?: string | null;
  createdBy?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  enteredOn?: string;
  /** Entered after its date (back-dated). */
  backDated?: boolean;
  /** Down the chain (requisition → PO, PO → receipt, receipt → bill): lines fully done / started of all lines. */
  chain?: { lines: number; done: number; started: number } | null;
  vatMode?: string | null;
}

export interface OpenSourceLine {
  lineId: string;
  documentId: string;
  number: string | null;
  /** PO lines of a Sub PO: the parent PO line it adds to. */
  parentLineId?: string | null;
  /** Inward gate pass: quantity already at the gate on other gate passes (no goods receipt yet). */
  atGate?: number;
  docDate: string;
  /** The document this one came from (a goods receipt's purchase order). */
  sourceNumber?: string | null;
  item: InvItemRef;
  qty: number;
  done: number;
  open: number;
  rate: number;
  discountPct: number;
  taxCodeId: string | null;
  /** Goods receipt lines: the purchase order's VAT mode (null = old document, VAT per line). */
  vatMode?: PoVatMode | null;
  /** Carried from the requisition: description, the demand change (red) and the special request. */
  description?: string;
  overrideReason?: string;
  systemQty?: number | null;
  special?: boolean;
  /** The last price paid for the item (any vendor), to fill the rate. */
  lastRate?: number | null;
}

/** Approved requisitions with quantity still to order (purchase order picker). */
export interface OpenRequisitionRow { id: string; number: string | null; docDate: string; requiredDate: string | null; kind: string | null; order: string | null; customer: string | null; department: string | null; lines: number; openLines: number }

export interface StockBalanceRow {
  itemId: string;
  code: string;
  name: string;
  category: string;
  uom: string;
  warehouse: string | null;
  qty: number;
  avgCost: number;
  value: number;
  minQty: number | null;
  reorderQty: number | null;
  belowMin: boolean;
}

export interface StockLedgerRow {
  id: string;
  date: string;
  documentId: string | null;
  number: string | null;
  docType: string;
  warehouse: string;
  description: string;
  qtyIn: number;
  qtyOut: number;
  unitCost: number;
  value: number;
  balanceQty: number;
  balanceValue: number;
}

export interface ClosingStockPreview {
  month: string;
  asOf: string;
  lines: { inventoryAccount: { id: string; code: string; name: string }; costCentre: string; valuation: number; glBalance: number; adjustment: number }[];
  total: number;
  posted: { documentId: string; number: string | null; postedAt: string } | null;
  mode: 'PERIODIC' | 'PERPETUAL';
}

// ---------- Physical stock count (cut-over and periodic counts) ----------

export const COUNT_MODES = ['ADJUSTMENT', 'OPENING'] as const;
export const COUNT_MODE_LABELS: Record<(typeof COUNT_MODES)[number], string> = { ADJUSTMENT: 'Adjust stock to the count (variances)', OPENING: 'Opening stock at go-live (counted quantities at cost)' };
export const stockCountSchema = z.object({
  docDate: isoDate,
  warehouseId: uuid('Select the warehouse'),
  categoryId: uuidOpt,
  mode: z.enum(COUNT_MODES),
  /** Also list items with no stock in the system (needed for the go-live count). */
  includeZero: z.boolean(),
  notes: opt(500),
});
export type StockCountInput = z.infer<typeof stockCountSchema>;
export const countEntriesSchema = z.object({
  entries: z.array(z.object({ lineId: z.string().uuid(), countedQty: z.preprocess((v) => (v === '' ? null : v), z.coerce.number().min(0, 'Cannot be negative').max(1e9).nullable()), unitCost: z.preprocess((v) => (v === '' || v == null ? null : v), z.coerce.number().min(0).max(1e9).nullable()).optional() })).max(2000),
});
export interface StockCountDetail {
  id: string;
  number: string | null;
  status: import('./documents').DocStatus;
  docDate: string;
  warehouse: { id: string; code: string; name: string };
  mode: (typeof COUNT_MODES)[number];
  notes: string;
  lines: { id: string; item: { id: string; code: string; name: string; uom: string; category: string }; systemQty: number; countedQty: number | null; variance: number | null; avgCost: number; unitCost: number | null; varianceValue: number | null }[];
  summary: { lines: number; counted: number; withVariance: number; varianceValue: number; countedValue: number };
  resultDocumentId: string | null;
  version: number;
  actions: import('./documents').WorkflowAction[];
}

/** Purchase & Inventory › Reorder: stock items whose stock and pipeline fall below the minimum. */
export interface ReorderRow {
  item: { id: string; code: string; name: string; uom: string; category: string };
  onHand: number;
  /** Open on POs (this system) and on requisitions not yet ordered (general / stock). */
  onOrder: number;
  onRequisition: number;
  /** On old-system POs (shown, not counted). */
  oldOnOrder: number;
  minQty: number;
  maxQty: number;
  reorderQty: number;
  /** Suggested: up to the maximum (or the minimum), at least the reorder quantity, in whole purchase units. */
  suggested: number;
  purchase: { uom: string; factor: number; qty: number } | null;
}

export const reorderRequisitionSchema = z.object({
  warehouseId: uuid('Select the warehouse'),
  requiredDate: isoDate,
  notes: opt(500),
  lines: z.array(z.object({ itemId: uuid('Select an item'), qty: positiveQty() })).min(1, 'Tick at least one item').max(300),
});
export type ReorderRequisitionInput = z.infer<typeof reorderRequisitionSchema>;

/** Change to an approved purchase order: new quantities / rates per line and the delivery date, with the reason. */
export const poChangeSchema = z.object({
  reason: z.string().trim().min(3, 'Say why the order changes').max(500),
  deliveryDate: optDate,
  lines: z.array(z.object({ lineId: z.string().uuid(), qty: qty(), rate: rate(), discountPct: z.coerce.number().min(0).max(100) })).min(1).max(300),
});
export type PoChangeInput = z.infer<typeof poChangeSchema>;
export const poChangeDecisionSchema = z.object({ approve: z.boolean(), note: z.string().trim().max(500).default('') });

export interface PoChangeRow {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reason: string;
  summary: string;
  requestedBy: string;
  requestedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string;
}


/** Stock held for an order (sales order, job or mattress production order), per item. */
export interface HeldStockRow {
  holdFor: { id: string; number: string | null; customer: string | null };
  item: { id: string; code: string; name: string; uom: string };
  held: number;
  received: number;
  issued: number;
  /** Order's own purchase price of what is held (receipts), for the costing choice. */
  unitCost: number | null;
  history: { at: string; kind: string; qty: number; reason: string; document: string | null; by: string | null }[];
}
export const heldReleaseSchema = z.object({ holdFor: z.string().uuid(), itemId: z.string().uuid(), qty: positiveQty(), reason: z.string().trim().min(3, 'Give the reason').max(300) });
export const heldMoveSchema = heldReleaseSchema.extend({ toOrderId: z.string({ error: 'Choose the order to move it to' }).uuid('Choose the order to move it to') });
export type HeldReleaseInput = z.infer<typeof heldReleaseSchema>;
export type HeldMoveInput = z.infer<typeof heldMoveSchema>;

// ---------- Sub PO (owner, 2026-11-02) ----------

/**
 * Sub PO: extra quantity on an approved purchase order (more arrived, or the vendor offers more). Same vendor, VAT mode,
 * terms and delivery place as the PO; only its items; its rate unless the reason says why; approved like a PO. The
 * extra is free stock, not demand of the order. Numbered after the PO (PO-…-S1).
 */
export const subPoSchema = z.object({
  reason: z.string({ error: 'Say why more is bought' }).trim().min(3, 'Say why more is bought (at least 3 characters)').max(300),
  /** Made from an inward gate pass / goods receipt (the extra that arrived). */
  gatePassId: uuidOpt,
  lines: z
    .array(z.object({
      parentLineId: uuid('Pick the PO line'),
      qty: positiveQty('Extra quantity'),
      rate: rate('Rate'),
      /** A rate different from the PO's: why. */
      rateReason: opt(200),
    }))
    .min(1, 'Add at least one line')
    .max(300),
});
export type SubPoInput = z.infer<typeof subPoSchema>;

/** What the old ERP recorded on a document, kept as it was (shown where the new links are missing). */
export interface InvLegacyInfo {
  so?: string | null;
  soDate?: string | null;
  job?: string | null;
  customer?: string | null;
  division?: string | null;
  section?: string | null;
  type?: string | null;
  reqNo?: string | null;
  reqDate?: string | null;
  poNo?: string | null;
  igpNo?: string | null;
  grnNo?: string | null;
  mrnNo?: string | null;
  billNo?: string | null;
  vehicle?: string | null;
  issuedTo?: string | null;
  reference?: string | null;
  createdBy?: string | null;
  createdAt?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  oldStatus?: string | null;
}

// ---------- Lead times (owner via coordinator, 2026-11-05) ----------

/** Where an item's lead time comes from: the supplier's own (wins), else the item's. */
export type LeadTimeSource = 'supplier' | 'item' | null;

/** One item's lead time for a supplier (or, with no supplier, its usual one: the item's, else its last PO supplier's). */
export interface LeadTimeRow {
  itemId: string;
  code: string;
  name: string;
  days: number | null;
  source: LeadTimeSource;
  /** The supplier the days belong to (chosen, or the item's last PO supplier). */
  supplier: string | null;
}

/** "Earliest date item X can be in stock" — for other modules (Production's Inquiry Studio). */
export interface EarliestInStock {
  itemId: string;
  code: string;
  name: string;
  uom: string;
  qty: number;
  /** Free today: on hand on every date from today on, less submitted issues / returns / adjustments. */
  freeNow: number;
  shortfall: number;
  leadTimeDays: number | null;
  leadTimeSource: LeadTimeSource;
  supplier: string | null;
  /** An approved open PO due on or after today whose quantity covers the shortfall (with what came before it). */
  onOrder: { po: string | null; deliveryDate: string; qty: number } | null;
  /** Today when free stock covers it; else the open PO date or today + lead time, whichever is earlier; null = unknown (no lead time, no PO). */
  earliestDate: string | null;
  basis: 'in_stock' | 'on_order' | 'lead_time' | 'unknown';
  /** One plain sentence saying why. */
  note: string;
}
